import React from "react";
import _ from 'lodash';
import clsx from "clsx";

import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {ExpandMoreOutlined} from "@material-ui/icons";

import Tooltip from "../../../../../../../components/Tooltip";
import WarningTooltip from "../../../../../../../components/WarningTooltip";
import {InfoIcon} from "../../../../../../../images";

import {useStyles} from "../styles";

const isValidValue = (value) => !!value || _.isNumber(value)

const getOptionStyling = (option) => {
  let styling = {};
  if (!_.isNil(option.depth)) {
    styling.paddingLeft = option.depth * 10;
  }

  return styling
}

const MAX_OPTIONS_FOR_SIMPLE_SELECT = 10;

const SelectFormElement = props => {
  const {
    value,
    label,
    required,
    options,
    onChange,
    disabled,
    custom_classes,
    error,
    placeholder,
    tooltip,
    withColorsIndicators,
    onChangeGetEvent,
    minimisedStyles,
    dataId,
    addEmptyOption,
    allowAutoComplete=false,
  } = props;
  const classes = useStyles();

  const hasValue = isValidValue(value);
  const useAutoComplete = allowAutoComplete && options.length > MAX_OPTIONS_FOR_SIMPLE_SELECT;

  const selectOptions = React.useMemo(() => {
    return addEmptyOption && !required ? [{id: 'empty', value: null, label: "\u00a0"}, ...options] : options
  }, [required, options, addEmptyOption]);

  const selectedOption = React.useMemo(
    () => (hasValue && selectOptions.find(o => o.value === value)) || null,
    [hasValue, value, selectOptions]
  );

  React.useEffect(() => {
    if (hasValue && !selectedOption){
      onChange(undefined); // clean answer as it's not in the options
    }
  }, [hasValue, selectedOption]);

  const handleChange = (event) => {
    onChange(onChangeGetEvent ? event : event.target.value);
  };

  const handleClick = (event) => {
    event.stopPropagation()
  };

  // Shared styling/props reused by both the Select and Autocomplete branches
  const rootClasses = clsx(classes.textFieldRoot, custom_classes.textFieldRoot, minimisedStyles && classes.minimisedSelect);

  const labelContent = label && <>{label}{tooltip && (<WarningTooltip title={tooltip} width={600} />)}</>;

  const inputPropsClasses = {
    root: clsx(classes.inputRoot, custom_classes.inputRoot),
    focused: classes.inputFocused,
  };

  const inputLabelProps = {
    shrink: true,
    classes: {
      root: clsx(classes.labelRoot, custom_classes.labelRoot),
      focused: classes.labelFocused,
      asterisk: classes.labelAsterisk,
    },
  };

  // Inner content of an option, shared between the Select MenuItem and Autocomplete's <li>
  const renderOptionContent = (option) => {
    const fontFamily = (option.depth === 1) ? 'Roboto-Bold' : 'Roboto-Regular';
    return (
      <span style={{ fontFamily: fontFamily }} data-type={option.type}>
        {withColorsIndicators && option.color && <span className={classes.colorIndicator} style={{ backgroundColor: option.color }} />}
        {option.label}
        {option.info && (
          <Tooltip title={option.info} className={classes.optionInfoTooltip} onClick={(event) => {event.preventDefault(); event.stopPropagation()}}>
            <span><InfoIcon /></span>
          </Tooltip>
        )}
      </span>
    );
  };

  const renderOption = (option) => {
    return (
      <MenuItem
        key={option.id}
        data-id={`select-option-${option.id}`}
        value={option.value}
        classes={{
          root: classes.selectMenuItemRoot
        }}
        style={getOptionStyling(option)}
        data-type={option.type}
        disabled={option.disabled}
      >
        {renderOptionContent(option)}
      </MenuItem>
    )
  }

  const renderValue = React.useCallback((value) => {

    const _getValueLabel = () => {
      const option = _.find(selectOptions, (option) => option.value === value);
      if (withColorsIndicators && option) {
        return <><span className={classes.colorIndicator} style={{ backgroundColor: option.color }} />{option.label}</>
      }
      return option && option.label
    };

    const _getPlaceholder = () => {
      return placeholder && (
        <span className={clsx(classes.selectPlaceholder, custom_classes.selectPlaceholder)}>{ placeholder }</span>
      )
    };

    return (isValidValue(value) && _getValueLabel()) || _getPlaceholder() || ''
  }, [selectOptions, placeholder]);

  if (!useAutoComplete) {
    return (
      <TextField
        select
        data-id={dataId}
        style={props.style || {}}
        disabled={disabled}
        label={labelContent}
        required={required}
        value={hasValue ? value : ''}
        onChange={handleChange}
        onClick={handleClick}
        error={!!error}
        helperText={error}
        classes={{
          root: rootClasses,
        }}
        InputProps={{
          classes: inputPropsClasses,
        }}
        InputLabelProps={inputLabelProps}
        SelectProps={{
          variant: 'outlined',
          displayEmpty: !!placeholder,
          renderValue: renderValue,
          classes: {
            select: classes.select
          },
          MenuProps: {
            classes: {
              paper: classes.selectMenuPaper
            }
          },
          IconComponent: ExpandMoreOutlined
        }}
      >
        {selectOptions.map(option => (renderOption(option)))}
      </TextField>
    );
  }

  const handleAutocompleteChange = (event, newOption) => {
    // simulate select event
    const syntheticTarget = {
      value: newOption ? newOption.value : undefined,
      getAttribute: (attr) => (attr === 'data-type' ? (newOption ? newOption.type : null) : null),
    };
    handleChange({
      ...event,
      target: syntheticTarget,
      nativeEvent: { ...(event && event.nativeEvent), target: syntheticTarget },
    });
  };

  const renderAutocompleteOption = (option) => {
    return (
      <div
        data-id={`select-option-${option.id}`}
        style={getOptionStyling(option)}
      >
        {renderOptionContent(option)}
      </div>
    );
  };

  return (
    <Autocomplete
      disableClearable={required}
      options={selectOptions}
      value={selectedOption}
      onChange={handleAutocompleteChange}
      getOptionLabel={(option) => (option && (option.text || option.label)) || ''}
      getOptionSelected={(option, val) => option.value === (val && val.value)}
      getOptionDisabled={(option) => !!option.disabled}
      renderOption={renderAutocompleteOption}
      disabled={disabled}
      data-id={dataId}
      noOptionsText={"keine Ergebnisse"}
      style={props.style || {}}
      classes={{
        root: rootClasses,
        popupIndicator: classes.autocompletePopupIndicator,
        paper: classes.selectMenuPaper,
        option: classes.selectMenuItemRoot,
      }}
      popupIcon={<ExpandMoreOutlined />}
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelContent}
          required={required}
          placeholder={placeholder}
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            classes: inputPropsClasses,
          }}
          InputLabelProps={inputLabelProps}
        />
      )}
    />
  )
};

SelectFormElement.defaultProps = {
  custom_classes: {},
  addEmptyOption: false, // add empty option if optional
};

export default SelectFormElement;
